import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { normalizePartNumber } from '../../utils/partNumber';

const MIN_SEARCH_LENGTH = 2;

const PartNumberSearchBar = ({ aircraft, onSelectResult }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return undefined;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const response = await axios.get('/api/components', {
          params: {
            partNumber: normalizePartNumber(trimmed),
            isActive: 'true',
            pageSize: 100,
            page: 1
          },
          signal: controller.signal
        });
        const components = response.data?.components ?? response.data ?? [];
        setResults(Array.isArray(components) ? components : []);
        setSearched(true);
      } catch (error) {
        if (error?.code !== 'ERR_CANCELED') {
          console.error('Part number search failed:', error);
          setResults([]);
          setSearched(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
  };

  const handleRowClick = (component) => {
    const aircraftId = component.aircraftId?._id || component.aircraftId;
    const matchedAircraft = aircraft.find((ac) => String(ac._id) === String(aircraftId));
    if (!matchedAircraft) return;

    const partNumber = component.partNumber || component.partId?.partNumber || query.trim();
    onSelectResult({
      aircraft: matchedAircraft,
      component,
      partNumber
    });
  };

  const getPartNumberDisplay = (component) =>
    component.partNumber || component.partId?.partNumber || '—';

  const getAircraftRegistration = (component) =>
    component.aircraftId?.registration || '—';

  const getAssemblyLabel = (component) => {
    const assembly = component.assemblyId;
    if (!assembly) return '—';
    const parts = [assembly.typeName, assembly.serialNumber].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  const uniqueAircraftCount = new Set(
    results.map((component) => String(component.aircraftId?._id || component.aircraftId || ''))
  ).size;

  const showResults = searched && query.trim().length >= MIN_SEARCH_LENGTH;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Search by Part Number (all aircraft)
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Enter part number to find matching components across all aircraft..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        inputProps={{ style: { fontFamily: 'monospace' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={18} sx={{ mr: query ? 0.5 : 0 }} />}
              {query && (
                <IconButton size="small" onClick={handleClear} aria-label="Clear part number search">
                  <Clear fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
      />

      {showResults && (
        <Box sx={{ mt: 1.5 }}>
          {results.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active components found for this part number.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {results.length} component(s) on {uniqueAircraftCount} aircraft
                </Typography>
                <Chip size="small" label="Click a row to open on Components tab" variant="outlined" />
              </Box>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Aircraft</TableCell>
                      <TableCell>Part Number</TableCell>
                      <TableCell>Component Code</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Assembly</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((component) => (
                      <TableRow
                        key={component._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(component)}
                      >
                        <TableCell>{getAircraftRegistration(component)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {getPartNumberDisplay(component)}
                        </TableCell>
                        <TableCell>{component.code || '—'}</TableCell>
                        <TableCell>{component.serialNumber || '—'}</TableCell>
                        <TableCell>{getAssemblyLabel(component)}</TableCell>
                        <TableCell>{component.status || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PartNumberSearchBar;
